// Backlog shapes (apps/api/src/modules/feedback/backlog.ts, planning.ts) and their labels.
import { useEffect, useState } from 'react';
import { authenticatedBlob } from '../../lib/api';
import type { PillTone } from '../../ui';
import type { FeedbackStatus } from '../feedback/types';

export type ItemKind = 'feature' | 'bug_fix' | 'improvement' | 'task' | 'epic';
export type ItemStatus = 'idea' | 'todo' | 'in_progress' | 'done' | 'wont_do';
export type Priority = 'urgent' | 'high' | 'medium' | 'low';
export type Size = 's' | 'm' | 'l';

export const KIND_LABEL: Record<ItemKind, string> = { feature: 'Feature', bug_fix: 'Bug', improvement: 'Improvement', task: 'Task', epic: 'Epic' };
export const KIND_TONE: Record<ItemKind, PillTone> = { feature: 'brand', bug_fix: 'bad', improvement: 'neutral', task: 'info', epic: 'purple' };
export const STATUS_LABEL: Record<ItemStatus, string> = { idea: 'Idea', todo: 'To do', in_progress: 'In progress', done: 'Done', wont_do: "Won't do" };
export const STATUS_TONE: Record<ItemStatus, PillTone> = { idea: 'purple', todo: 'neutral', in_progress: 'info', done: 'ok', wont_do: 'neutral' };
export const PRIORITY_LABEL: Record<Priority, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
export const PRIORITY_TONE: Record<Priority, PillTone> = { urgent: 'bad', high: 'warn', medium: 'neutral', low: 'neutral' };
export const SIZE_LABEL: Record<Size, string> = { s: 'S', m: 'M', l: 'L' };
export const FOUND_IN_LABEL = { production: 'Production', test: 'Test', local: 'Local' } as const;
export const KINDS = Object.keys(KIND_LABEL) as ItemKind[];
export const STATUSES = Object.keys(STATUS_LABEL) as ItemStatus[];
export const PRIORITIES = Object.keys(PRIORITY_LABEL) as Priority[];
export const PRIORITY_RANK: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

export type ItemRequest = {
  id: string; reference: string; title: string; status: FeedbackStatus; cell_id: string; firm_key: string; firm_name: string | null;
  submitted_by_name: string; estimated_completion: string | null; estimate_source: 'manual' | 'release' | null; need_by: string | null;
};

export type Item = {
  id: string; reference: string; kind: ItemKind; title: string; status: ItemStatus; priority: Priority; size: Size | null;
  area: string | null; found_in: keyof typeof FOUND_IN_LABEL | null; labels: string[]; sort_order: number;
  release_id: string | null; release_name: string | null; release_kind: 'release' | 'hotfix' | null;
  release_status: 'planned' | 'in_progress' | 'shipped' | 'cancelled' | null; release_target_date: string | null;
  release_shipped_at: string | null; release_announced_in_id: string | null;
  owner_id: string | null; owner_name: string | null; archived_at: string | null; duplicate_of_id: string | null; told_early_at: string | null;
  created_at: string; updated_at: string; requests: ItemRequest[];
  attachment_count: number; cover_id: string | null; comment_count: number; checklist_total: number; checklist_done: number;
  /** The epic or story it sits under (migration 021). */
  parent_id: string | null; parent: { reference: string; title: string; kind: ItemKind } | null;
  /** Its open items (an epic) or sub-tasks (a story): Won't do counts toward neither. */
  children_total: number; children_done: number;
  /** The firm request it was made from, when it arrived from Feedback. */
  origin_request_id: string | null;
};

export type Attachment = {
  id: string; comment_id: string | null; previous_id: string | null; filename: string; mime_type: string; size_bytes: number;
  scan: 'pending' | 'clean' | 'infected' | 'failed'; is_screenshot: boolean; annotated: boolean; caption: string | null;
  is_cover: boolean; in_release_notes: boolean; replaced_at: string | null; created_at: string; uploaded_by_name: string;
};
export type FirmAttachment = {
  id: string; request_id: string; request_reference: string; filename: string; mime_type: string; size_bytes: number;
  scan: Attachment['scan']; is_screenshot: boolean; created_at: string; uploaded_by_name: string; firm_name: string | null;
};

export type ItemDetail = Item & {
  description: string | null; steps: string | null; expected: string | null; actual: string | null;
  created_by_name: string | null; duplicate_of_reference: string | null;
  attachments: Attachment[]; firm_attachments: FirmAttachment[];
  checklist: Array<{ id: string; text: string; owner_id: string | null; owner_name: string | null; done_at: string | null }>;
  comments: Array<{ id: string; body: string; author_id: string; author_name: string; created_at: string }>;
  events: Array<{ id: string; actor_name: string | null; type: string; payload: Record<string, unknown>; at: string }>;
  /** Who follows the item (My work shows them its new comments). */
  watchers: Person[];
  duplicates: Array<{ id: string; reference: string; title: string }>;
  children: Item[];
};

export type Person = { id: string; name: string };
export type OpenRelease = { id: string; name: string; kind: 'release' | 'hotfix'; status: string; target_date: string | null };
export type EpicOption = { id: string; reference: string; title: string };
/** `epics`: open epics, for the Epic pickers (missing from older responses and test stubs). */
export type BacklogMeta = { team: Person[]; releases: OpenRelease[]; hotfix_box_id: string; me: string; epics?: EpicOption[] };
/** `following`: ids of the items I follow (missing from older responses and test stubs). */
export type BacklogData = BacklogMeta & { items: Item[]; following?: string[] };

export const isShipped = (i: Pick<Item, 'release_status'>) => i.release_status === 'shipped';
/** Epic > story (Feature, Bug, Improvement, Task) > sub-task. A sub-task sits under a story. */
export type Level = 'epic' | 'story' | 'subtask';
export const levelOf = (i: Pick<Item, 'kind' | 'parent'>): Level => (i.kind === 'epic' ? 'epic' : i.parent && i.parent.kind !== 'epic' ? 'subtask' : 'story');
/** Epics aren't scheduled (their items are) and a sub-task ships with its parent. */
export const canSchedule = (i: Pick<Item, 'kind' | 'parent'>) => levelOf(i) === 'story';
export const isOpen = (i: Item) => !isShipped(i) && i.status !== 'wont_do' && !i.archived_at;

/** "Next hotfix", "Hotfix 4.2.8 · live", "Release 4.3" or "Not scheduled". */
/** "Hotfix 30" for a rolled hotfix named "30" (or "Hotfix 30"), so it never reads as a bare number. */
export const hotfixName = (name: string | null | undefined) => (!name ? 'Hotfix' : /^hotfix\b/i.test(name.trim()) ? name.trim() : `Hotfix ${name.trim()}`);

export function whereLabel(i: Pick<Item, 'release_id' | 'release_name' | 'release_kind' | 'release_status'>): string {
  if (!i.release_id) return 'Not scheduled';
  if (i.release_kind === 'hotfix') return i.release_status === 'shipped' ? `${hotfixName(i.release_name)} · live` : 'Next hotfix';
  return i.release_name ?? 'Release';
}

export const releaseOption = (r: OpenRelease) => r.kind === 'hotfix'
  ? 'Next hotfix (goes live on the next roll)'
  : `${r.name}${r.target_date ? ` · target ${r.target_date}` : ''}`;

/**
 * Object URL for an admin file (image bytes need the auth header, so no plain <img src>). Cached per path,
 * most recent last; past CACHE_LIMIT the oldest blob is released so a long session doesn't hold every screenshot.
 */
const blobCache = new Map<string, Promise<string>>();
const CACHE_LIMIT = 80;
export function adminImageUrl(path: string): Promise<string> {
  let p = blobCache.get(path);
  if (p) { blobCache.delete(path); blobCache.set(path, p); return p; }
  p = authenticatedBlob(path).then((b) => URL.createObjectURL(b));
  p.catch(() => blobCache.delete(path));
  blobCache.set(path, p);
  while (blobCache.size > CACHE_LIMIT) {
    const [oldest, url] = blobCache.entries().next().value as [string, Promise<string>];
    blobCache.delete(oldest);
    url.then((u) => URL.revokeObjectURL(u), () => {});
  }
  return p;
}
export function useAdminImage(path: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    if (path) adminImageUrl(path).then((u) => { if (live) setUrl(u); }, () => {});
    return () => { live = false; };
  }, [path]);
  return url;
}
export const itemFile = (itemId: string, fileId: string) => `/api/admin/backlog/${itemId}/attachments/${fileId}/content`;
export const requestFile = (requestId: string, fileId: string) => `/api/admin/feedback/${requestId}/attachments/${fileId}/content`;

/** Images on the clipboard of a paste event, named like "screenshot-2026-10-27-0912.png". */
export function pastedImages(e: ClipboardEvent | React.ClipboardEvent): File[] {
  const items = [...(e.clipboardData?.items ?? [])].filter((x) => x.kind === 'file' && x.type.startsWith('image/'));
  const stamp = new Date().toISOString().slice(0, 16).replace('T', '-').replace(':', '');
  return items.map((x, n) => {
    const f = x.getAsFile()!;
    const ext = f.type.split('/')[1] === 'jpeg' ? 'jpg' : f.type.split('/')[1] ?? 'png';
    return new File([f], f.name && f.name !== 'image.png' ? f.name : `screenshot-${stamp}${n ? `-${n + 1}` : ''}.${ext}`, { type: f.type });
  });
}

/** The list order the item page steps through with J / K (written by the backlog list). */
export const ORDER_KEY = 'upm.backlog.order';

export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const FILE_ACCEPT = '.png,.jpg,.jpeg,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt';
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Appends files to a form the way the API expects: images as `screenshots`, everything else as `files`. */
export function appendFiles(form: FormData, files: Array<{ file: File; caption?: string; annotated?: boolean }>) {
  const shots = files.filter((f) => IMAGE_TYPES.includes(f.file.type));
  const rest = files.filter((f) => !IMAGE_TYPES.includes(f.file.type));
  // Busboy reports parts in order; screenshots first so files_meta lines up with them.
  for (const f of [...shots, ...rest]) form.append(IMAGE_TYPES.includes(f.file.type) ? 'screenshots' : 'files', f.file, f.file.name);
  form.append('files_meta', JSON.stringify([...shots, ...rest].map((f) => ({ ...(f.caption ? { caption: f.caption } : {}), ...(f.annotated ? { annotated: true } : {}) }))));
}
